import { amountLineLabel, REPORT_LABELS, type SnHeader, type SnModel } from '@pz/core';
import { d } from '../lib/format';

/**
 * Print layout of a Stundennachweis (browser print, "Als PDF speichern"). Hidden on screen; the editor is
 * hidden in print. Uses the texts currently shown in the editor (also unsaved edits) and fixed colours,
 * so the PDF looks the same in light and dark mode. screen: the same layout shown on screen (print preview of the
 * demo, where the print dialog may be blocked); it is not printed itself.
 */
export function SnPrint({ model, header, dayNotes, screen }: { model: SnModel; header: SnHeader; dayNotes: Record<string, string>; screen?: boolean }) {
  const lang = model.settings.language;
  const L = REPORT_LABELS[lang];
  const num = (minutes: number) =>
    (minutes / 60).toLocaleString(lang === 'de' ? 'de-DE' : 'en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  const money = (cents: number | null) =>
    cents === null ? '' : (cents / 100).toLocaleString(lang === 'de' ? 'de-DE' : 'en-GB', { style: 'currency', currency: 'EUR' });
  const persons = model.persons;
  const matrix = model.settings.layout === 'matrix';
  const info: [string, string][] = [
    [L.customer, header.customer],
    [L.customerAddress, (header.customerAddress ?? '').split('\n').map((x) => x.trim()).filter(Boolean).join(', ')],
    [L.customerContact, header.customerContact ?? ''],
    [L.project, [header.projectLabel, header.projectNumber ? `(${header.projectNumber})` : ''].filter(Boolean).join(' ')],
    [L.customerReference, header.customerReference ?? ''],
    [L.consultant, header.consultant],
    [L.partner, header.partner],
    [L.period, header.periodLabel],
    [L.site, header.siteLocation],
  ].filter(([, v]) => !!v) as [string, string][];
  const cell = 'border border-[#94a3b8] px-1.5 py-1 align-top';
  const head = 'border border-[#94a3b8] bg-[#e2e8f0] px-1.5 py-1 text-left font-semibold';
  const signLine = (value: string, label: string) => (
    <div className="flex flex-col">
      <div className="min-h-[1.4em] border-b border-[#111] pb-0.5">{value}</div>
      <div className="pt-0.5 text-[8.5pt] font-semibold">{label}</div>
    </div>
  );
  return (
    <div className={screen ? 'min-w-[640px] text-[9.5pt] leading-snug text-[#111] print:hidden' : 'hidden text-[9.5pt] leading-snug text-[#111] print:block'}>
      {!screen && <style>{`@page { size: A4 ${matrix && persons.length > 4 ? 'landscape' : 'portrait'}; margin: 12mm 12mm 14mm; }`}</style>}
      {header.issuerName && <div className="text-[8pt] text-[#475569]">{[header.issuerName, header.issuerAddress].filter(Boolean).join(' · ')}</div>}
      <h1 className="mt-1 mb-2 text-[15pt] font-bold text-[#1e3a5f]">{header.title || L.stundennachweis}</h1>
      <table className="mb-3 border-collapse">
        <tbody>
          {info.map(([label, value]) => (
            <tr key={label}>
              <td className="py-0.5 pr-6 font-semibold">{label}</td>
              <td className="py-0.5">{value}</td>
            </tr>
          ))}
        </tbody>
      </table>
      {matrix ? (
        <table className="w-full border-collapse">
          <thead className="table-header-group">
            <tr>
              <th className={head}>{L.date}</th>
              {persons.map((p) => (
                <th key={p.id} className={`${head} text-right`}>
                  {p.name}
                  {p.discipline && <span className="block font-normal">{p.discipline}</span>}
                </th>
              ))}
              <th className={`${head} text-right`}>{L.sum}</th>
              <th className={`${head} w-[45%]`}>{L.activity}</th>
            </tr>
          </thead>
          <tbody>
            {model.days.map((day) => {
              const text = dayNotes[day.date] ?? day.generatedText;
              const bg = day.holiday ? 'bg-[#fff3e0]' : day.weekend ? 'bg-[#f1f5f9]' : '';
              return (
                <tr key={day.date} className={`break-inside-avoid ${bg}`}>
                  <td className={`${cell} whitespace-nowrap`}>{day.label}</td>
                  {persons.map((p) => (
                    <td key={p.id} className={`${cell} text-right tabular-nums`}>
                      {day.minutes[p.id] ? num(day.minutes[p.id]) : ''}
                    </td>
                  ))}
                  <td className={`${cell} text-right font-semibold tabular-nums`}>{day.totalMinutes ? num(day.totalMinutes) : ''}</td>
                  <td className={`${cell} whitespace-pre-wrap`}>{text || day.holiday || ''}</td>
                </tr>
              );
            })}
            <tr className="break-inside-avoid bg-[#e2e8f0] font-semibold">
              <td className={cell}>{L.totalHours}</td>
              {persons.map((p) => (
                <td key={p.id} className={`${cell} text-right tabular-nums`}>
                  {num(p.minutes)}
                </td>
              ))}
              <td className={`${cell} text-right tabular-nums`}>{num(model.totalMinutes)}</td>
              <td className={cell} />
            </tr>
          </tbody>
        </table>
      ) : (
        <table className="w-full border-collapse">
          <thead className="table-header-group">
            <tr>
              <th className={head}>{L.date}</th>
              <th className={head}>{L.person}</th>
              <th className={head}>{L.task}</th>
              <th className={`${head} w-[40%]`}>{L.activity}</th>
              <th className={`${head} text-right`}>{L.hours}</th>
            </tr>
          </thead>
          <tbody>
            {model.rows
              .filter((r) => !r.excluded)
              .map((r) => (
                <tr key={r.entryId} className="break-inside-avoid">
                  <td className={`${cell} whitespace-nowrap`}>{d(r.date)}</td>
                  <td className={cell}>{r.personName}</td>
                  <td className={cell}>{r.taskLabel}</td>
                  <td className={`${cell} whitespace-pre-wrap`}>{r.description}</td>
                  <td className={`${cell} text-right tabular-nums`}>{num(r.minutes)}</td>
                </tr>
              ))}
            <tr className="break-inside-avoid bg-[#e2e8f0] font-semibold">
              <td className={cell} colSpan={4}>
                {L.totalHours}
              </td>
              <td className={`${cell} text-right tabular-nums`}>{num(model.totalMinutes)}</td>
            </tr>
          </tbody>
        </table>
      )}
      {model.settings.showAmounts && model.totalAmountCents !== null && (
        <table className="mt-3 border-collapse">
          <thead>
            <tr>
              {[L.person, L.hours, L.rate, L.amount].map((x) => (
                <th key={x} className={head}>
                  {x}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {model.amounts.map((l) => (
              <tr key={`${l.personId}${l.from}`}>
                <td className={cell}>{amountLineLabel(l, model.amounts)}</td>
                <td className={`${cell} text-right tabular-nums`}>{num(l.minutes)}</td>
                <td className={`${cell} text-right tabular-nums`}>{l.rateCents !== null ? money(l.rateCents) : L.noRate}</td>
                <td className={`${cell} text-right tabular-nums`}>{money(l.amountCents)}</td>
              </tr>
            ))}
            <tr className="font-semibold">
              <td className={cell} colSpan={3}>
                {L.net}
              </td>
              <td className={`${cell} text-right tabular-nums`}>{money(model.totalAmountCents)}</td>
            </tr>
          </tbody>
        </table>
      )}
      {header.note && (
        <p className="mt-3">
          <span className="font-semibold">{L.note}:</span> {header.note}
        </p>
      )}
      <div className="mt-8 grid break-inside-avoid grid-cols-2 gap-x-16 gap-y-7">
        {signLine([header.place, header.documentDate ? d(header.documentDate) : ''].filter(Boolean).join(', '), L.placeDate)}
        {signLine('', L.placeDate)}
        {signLine('', `${L.signature} ${L.contractor}`)}
        {signLine('', `${L.signature} ${L.client}`)}
        {signLine(header.providerSignatureName, `${L.name} ${L.contractor}`)}
        {signLine(header.clientSignatureName, `${L.name} ${L.client}`)}
      </div>
    </div>
  );
}
